import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Divider,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useParticipantStatuses } from '../hooks/useParticipantStatuses';
import { getAllParticipants, getParticipantStepCount, isLeadBooker } from '../utils/booking';
import { getParticipantStepStatus } from '../utils/participantStatus';
import { ApiContext } from './ApiProvider';
import ParticipantsList from './ParticipantsList';
import StyledButton from './StyledButton';
import WaiverList, { getEffectiveWaiverLang } from './WaiverList';


const PreCheckinStep = ({ onComplete, onCancel }) => {
    const { booking, isActive, preCheckSettings, preCheckin } = useContext(ApiContext);
    const { enqueueSnackbar } = useSnackbar();
    const [promoConsent, setPromoConsent] = useState(false);
    const [promoConsentReady, setPromoConsentReady] = useState(false);
    const [error, setError] = useState(null);
    // The language picked in a waiver's selector; until then the Lead Booker's
    const [chosenWaiverLang, setChosenWaiverLang] = useState(null);

    // Use new status management
    const { areAllReady, areAllCheckedIn, statuses } = useParticipantStatuses();

    // Use waivers from pre_check_settings instead of root
    const waivers = preCheckSettings?.waivers || [];
    const waiverEnabled = preCheckSettings?.waiver_enabled !== false; // Default to true if not specified
    const isThereAnyWaiver = waiverEnabled && waivers.length > 0;

    // Initialize promo consent from booking once it's available to avoid flicker. Only once: the
    // booking is re-read in the background (CP-6549), which must not undo the guest's choice.
    useEffect(() => {
        if (booking && !promoConsentReady) {
            const initial = booking.user && booking.user.allow_ads !== undefined
                ? booking.user.allow_ads
                : false;
            setPromoConsent(initial);
            setPromoConsentReady(true);
        }
    }, [booking, promoConsentReady]);

    if (!booking) {
        return <CircularProgress />;
    }

    const requiredWaivers = waiverEnabled ? waivers : [];
    // Shown and signed in this language: the Lead Booker reads the waivers for the whole party
    const waiverLang = chosenWaiverLang || booking.user?.lang;

    // Check if all participants are ready for pre-checkin
    const allReady = areAllReady();
    const allParticipants = getAllParticipants(booking);
    const participantStepCount = getParticipantStepCount(booking, preCheckSettings);

    // Get not ready steps based on session-aware statuses
    const notReadySteps = allParticipants
        .slice(0, participantStepCount)
        .map((participant, index) => {
            const isLeadBookerStep = isLeadBooker(index);
            const status = statuses[index] || 'incomplete';
            // Lead Booker cannot be skipped — must be completed or saved
            const ready = isLeadBookerStep
                ? (status === 'completed' || status === 'saved')
                : (status === 'completed' || status === 'saved' || status === 'skipped');

            return {
                index,
                ready,
                label: isLeadBookerStep ? 'Lead Booker' : `Guest ${index}`
            };
        })
        .filter(step => !step.ready)
        .map(step => step.label);

    // Check if all participants are already pre-checked-in
    const allAlreadyCheckedIn = areAllCheckedIn();
    const isEditAfterCheckinAllowed = preCheckSettings?.allow_edit_after_checkin === true;
    // Consider participant as processed if pre-checked-in OR skipped
    const allCheckedOrSkipped = allParticipants.slice(0, participantStepCount).every((participant, index) => {
        const isLeadBookerStep = isLeadBooker(index);
        const fields = isLeadBookerStep
            ? preCheckSettings?.lead_booker_fields || []
            : preCheckSettings?.party_member_fields || [];
        const status = getParticipantStepStatus(participant, fields, null, booking, preCheckSettings, false);
        const isAlreadyCheckedIn = isLeadBookerStep ? booking?.checked_in : participant?.checked_in;
        return isAlreadyCheckedIn || status === 'skipped';
    });
    const isEditAfterCheckinMode = allCheckedOrSkipped && isEditAfterCheckinAllowed;

    console.debug('PreCheckinStep: notReadySteps:', notReadySteps);

    // Check if there are any participants that can be pre-checked-in (not already pre-checked-in and not skipped)
    const hasParticipantsToCheckIn = allParticipants.slice(0, participantStepCount).some((participant, index) => {
        const isLeadBookerStep = isLeadBooker(index);
        const fields = isLeadBookerStep
            ? preCheckSettings?.lead_booker_fields || []
            : preCheckSettings?.party_member_fields || [];
        const status = getParticipantStepStatus(participant, fields, null, booking, preCheckSettings, false);

        // Check actual pre-check-in status (not field completion status)
        const isAlreadyCheckedIn = isLeadBookerStep
            ? booking?.checked_in
            : participant?.checked_in;

        // Can pre-check-in if not already pre-checked-in and not skipped
        return !isAlreadyCheckedIn && status !== 'skipped';
    });

    // Combine all conditions for enabling the button
    const canProceed = allReady && !allAlreadyCheckedIn && hasParticipantsToCheckIn;

    // Build tooltip text for missing requirements
    const missingRequirements = [];
    if (allAlreadyCheckedIn) {
        missingRequirements.push('All participants are already pre-checked-in');
    } else if (!hasParticipantsToCheckIn) {
        missingRequirements.push('No participants available to pre-check-in (all are either pre-checked-in or skipped)');
    } else {
        if (!allReady && notReadySteps.length > 0) {
            missingRequirements.push(`Complete steps: ${notReadySteps.join(', ')}`);
        }
    }

    const tooltipText = isEditAfterCheckinMode
        ? ''
        : (missingRequirements.length > 0
            ? (allAlreadyCheckedIn
                ? missingRequirements[0]
                : missingRequirements.join('; '))
            : '');

    const handlePromoConsentChange = (event) => {
        setPromoConsent(event.target.checked);
    };

    const handlePreCheckin = async () => {
        console.debug("Finalizing pre-check-in...");
        console.debug("Promo Consent:", promoConsent);
        // no-op

        setError(null);

        try {
            if (isEditAfterCheckinMode) {
                const payload = {
                    user: {
                        id: booking.user.id,
                        allow_ads: promoConsent
                    }
                };

                console.debug('Saving marketing consent only:', payload);
                await preCheckin(payload);
                enqueueSnackbar('Changes saved successfully', { variant: 'success' });
                return; // stay on the page
            }
            // Build waiver signatures payload - group waivers by participant (excluding skipped)
            const allParticipants = getAllParticipants(booking);
            const stepCount = getParticipantStepCount(booking, preCheckSettings);
            const waiverSignatures = allParticipants
                .slice(0, stepCount)
                .filter((participant, index) => {
                    const isLeadBookerStep = isLeadBooker(index);
                    const fields = isLeadBookerStep
                        ? preCheckSettings?.lead_booker_fields || []
                        : preCheckSettings?.party_member_fields || [];
                    const status = getParticipantStepStatus(participant, fields, null, booking, preCheckSettings, false);
                    return status !== 'skipped';
                })
                .map((participant, originalIndex) => {
                    // Find the original index in the full participants array
                    const actualIndex = allParticipants.findIndex(p => p === participant);

                    const participantWaivers = requiredWaivers.map(waiver => {
                        const lang = getEffectiveWaiverLang(waiver, waiverLang) || 'en';

                        return {
                            waiver_id: waiver.id,
                            lang: lang,
                            first_name: participant.user.first_name,
                            last_name: participant.user.last_name,
                            email: participant.user.email,
                            phone: participant.user.phone || '',
                            agreed: true
                        };
                    });

                    // Use user_id for Lead Booker (index 0), participant_id for others
                    const participantSignature = {
                        waivers: participantWaivers
                    };

                    if (actualIndex === 0) {
                        participantSignature.user_id = participant.user?.id || participant.id;
                    } else {
                        participantSignature.participant_id = participant.id;
                    }

                    return participantSignature;
                });

            // Build bookings_to_check_in array (excluding skipped participants)
            const bookingsToCheckIn = [];

            // Add booking.id for Lead Booker (index 0) if not skipped
            const leadBookerFields = preCheckSettings?.lead_booker_fields || [];
            const leadBookerStatus = getParticipantStepStatus(allParticipants[0], leadBookerFields, null, booking, preCheckSettings, false);
            if (leadBookerStatus !== 'skipped') {
                bookingsToCheckIn.push(booking.id);
            }

            if (preCheckSettings?.collect_party_members_info !== false) {
                // Add booking.party[].id for each party member (if not skipped)
                // For virtual participants (id: null), we still include null to signal backend to create sub-booking
                (booking.party || []).forEach((participant, index) => {
                    const participantIndex = index + 1; // Party members start from index 1
                    const partyMemberFields = preCheckSettings?.party_member_fields || [];
                    const partyMemberStatus = getParticipantStepStatus(allParticipants[participantIndex], partyMemberFields, null, booking, preCheckSettings, false);
                    if (partyMemberStatus !== 'skipped') {
                        bookingsToCheckIn.push(participant.id);
                    }
                });
            }

            const payload = {
                waiver_signatures: waiverSignatures,
                user: {
                    id: booking.user.id,
                    allow_ads: promoConsent
                },
                bookings_to_check_in: bookingsToCheckIn
            };

            console.debug(`Sending waiver signatures for ${allParticipants.length} participants with ${requiredWaivers.length} waivers each:`, payload);

            const response = await preCheckin(payload);

            console.debug("Pre-checkin successful, response:", response);
            console.debug("After preCheckin - Lead Booker checked_in:", booking?.checked_in);
            console.debug("After preCheckin - Party members checked_in:", booking?.party?.map(p => ({ id: p.id, checked_in: p.checked_in })));
            console.debug("Pre-checkin successful, proceeding to success page");
            onComplete();
        } catch (error) {
            console.error("Pre-checkin failed:", error);
            setError(error.message || 'Checkin failed. Please try again.');
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 1, borderRadius: 2 }}>
            {/* Participants List - when collect_party_members_info is false, show only Lead Booker */}
            <Box mb={1}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
                    Pre-check-in for:
                </Typography>
                <ParticipantsList
                    participants={
                        preCheckSettings?.collect_party_members_info !== false
                            ? getAllParticipants(booking)
                            : [getAllParticipants(booking)[0]] // Only Lead Booker
                    }
                    participantStatuses={statuses}
                />
            </Box>

            {/* Show waivers only if waiver_enabled is true */}
            {waiverEnabled && waivers.length > 0 && (
                <WaiverList
                    waivers={waivers}
                    lang={waiverLang}
                    onLanguageChange={setChosenWaiverLang}
                />
            )}

            <Divider sx={{ mx: -3 }} />

            {/* Default promotional consent - keeping this for backward compatibility */}
            {promoConsentReady && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={promoConsent}
                                onChange={handlePromoConsentChange}
                                name="promoConsent"
                                disabled={isActive || (!isEditAfterCheckinMode && allCheckedOrSkipped)}
                            />
                        }
                        label={<Typography variant="body2">I allow my contact information to be used for promotional tours and offers</Typography>}
                    />

                    <Typography variant="caption2" display="block" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', textAlign: 'left' }}>
                        *By proceeding with pre-check-in, you confirm that you have permission to pre-check-in all members of your party and to receive tour-related communication.
                    </Typography>
                </Box>
            )}

            {/* Action Buttons: Stacked Vertically */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                )}

                <Tooltip title={tooltipText} arrow placement="top">
                    <span>
                        <StyledButton
                            variant="contained"
                            onClick={handlePreCheckin}
                            disabled={isActive || (!isEditAfterCheckinMode && !canProceed)}
                            fullWidth
                        >
                            {isActive
                                ? <CircularProgress size={24} />
                                : (isEditAfterCheckinMode
                                    ? 'Save changes'
                                    : (!allAlreadyCheckedIn && isThereAnyWaiver ? 'Sign Waiver(s) and Pre-check-in' : 'Pre-check-in'))}
                        </StyledButton>
                    </span>
                </Tooltip>
                {!allAlreadyCheckedIn && (
                    <StyledButton
                        variant="outlined"
                        onClick={onCancel}
                        disabled={isActive}
                        fullWidth
                    >
                        Cancel
                    </StyledButton>
                )}
            </Box>
        </Paper>
    );
};

export default PreCheckinStep; 